import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useUser } from '@/contexts/UserContext';
import { useUserRole } from '@/hooks/useUserRole';
import { User, MapPin, Calendar, Mail, Settings } from 'lucide-react';
import { AdminProfileSection } from '@/components/admin/AdminProfileSection';

export const Profile: React.FC = () => {
  const { currentUser, accountType } = useUser();
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();

  if (!currentUser) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Please log in to view your profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Admin Section */}
        {isAdmin && <AdminProfileSection />}

        {/* Header Card */}
        <Card>
          <CardHeader className="pb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="relative">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={currentUser.avatar_url || undefined} alt={currentUser.display_name} />
                  <AvatarFallback className="text-2xl">
                    <User className="h-12 w-12" />
                  </AvatarFallback>
                </Avatar>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute -bottom-2 -right-2 h-9 w-9 rounded-full shadow-md bg-background"
                  onClick={() => navigate('/settings')}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold">{currentUser.display_name}</h1>
                  {currentUser.is_verified && (
                    <Badge variant="default">Verified</Badge>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    <span className="capitalize">{accountType}</span>
                  </div>
                  {currentUser.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      <span>{currentUser.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>Joined {new Date(currentUser.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                
                {currentUser.bio && (
                  <p className="text-muted-foreground mt-4">{currentUser.bio}</p>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Email</span>
              <span className="font-medium">{currentUser.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Account Type</span>
              <Badge variant="outline" className="capitalize">{accountType}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Setup Completed</span>
              <Badge variant={currentUser.setup_completed ? "default" : "secondary"}>
                {currentUser.setup_completed ? "Yes" : "No"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Account Status */}
        <Card>
          <CardHeader>
            <CardTitle>Account Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Welcome to your profile! Complete your account setup to unlock all platform features.
            </p>
            
            {!currentUser.setup_completed && (
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-orange-800 text-sm">
                  <strong>Setup Required:</strong> Complete your profile setup to access all features.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
  );
};